import Image from 'next/image'
import React, { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { VideoProps } from '~types'
import styles from './Video.module.scss'
import { useIsIntersecting } from 'utils/hooks/useIsIntersecting'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { useWindowSize } from 'utils/hooks'
import { useRouter } from 'next/router'

enum VideoPlaybackMode {
  SUCCESS = 'success',
  FAILED = 'failed',
  PENDING = 'pending'
}

const Video = ({
  autoPlay = true,
  backgroundVideoXl,
  backgroundVideoMd,
  backgroundVideoSm,
  className,
  posterImageXl,
  posterImageMd,
  posterImageSm,
  videoLoop,
  externalVideoRef,
  preload = 'none',
  handlePlaybackFailed,
  isPaused
}: VideoProps) => {
  const router = useRouter()
  const internalVideoRef = useRef<HTMLVideoElement>(null)
  const backgroundVideoRef = externalVideoRef || internalVideoRef
  const [videoPlaybackMode, setVideoPlaybackMode] = useState<string>(VideoPlaybackMode.PENDING)
  const { isBreakpoint: isDesktop } = useWindowSize('lg', '>')
  const { isBreakpoint: isTabletAndUp } = useWindowSize('md', '>=')

  const fallbackImage = posterImageXl ?? posterImageMd ?? posterImageSm
  const fallbackVideo = backgroundVideoXl ?? backgroundVideoMd ?? backgroundVideoSm

  const image = (isDesktop ? posterImageXl : isTabletAndUp ? posterImageMd : posterImageSm) ?? fallbackImage
  const video = (isDesktop ? backgroundVideoXl : isTabletAndUp ? backgroundVideoMd : backgroundVideoSm) ?? fallbackVideo

  const isElementIntersecting = useIsIntersecting({
    ref: backgroundVideoRef,
    observerOptions: {
      root: null,
      rootMargin: '0px',
      threshold: 0.01
    }
  })
  const videoKey = `video-${video?.url}`

  const playVideo = async () => {
    if (!backgroundVideoRef.current) return
    try {
      await backgroundVideoRef.current.play()
      backgroundVideoRef.current.poster = ''
      setVideoPlaybackMode(VideoPlaybackMode.SUCCESS)
      handlePlaybackFailed?.(false)
    } catch (error) {
      setVideoPlaybackMode(VideoPlaybackMode.FAILED)
      handlePlaybackFailed?.(true)
    }
  }

  useEffect(() => {
    const videoElement = backgroundVideoRef.current
    if (!videoElement) return

    if (isElementIntersecting && !isPaused) {
      playVideo()
    } else {
      videoElement.pause()
    }

    return () => {
      if (videoElement) {
        videoElement.pause()
        videoElement.currentTime = 0
      }
    }
  }, [isElementIntersecting, backgroundVideoRef, isPaused, router.asPath])

  if (!image && !video) return null

  return (
    <div className={cx(styles['full-screen-video-container'], className)}>
      {videoPlaybackMode === VideoPlaybackMode.FAILED ? (
        <div className={styles['poster-container']}>
          {image && (
            <Image
              alt={image.altText ?? ''}
              src={image.url}
              width='0'
              height='0'
              sizes='100vw'
              quality={NEXT_IMAGE_QUALITY}
            />
          )}
        </div>
      ) : (
        video && (
          <video
            key={videoKey}
            autoPlay={autoPlay}
            loop={videoLoop}
            ref={backgroundVideoRef}
            muted
            playsInline={true}
            poster={image?.url}
            disableRemotePlayback
            preload={preload}
          >
            <source src={video.url} type='video/mp4' />
          </video>
        )
      )}
    </div>
  )
}
export default Video
